import { useState } from 'react';
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import CircleOutlinedIcon from '@mui/icons-material/CircleOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import { Box, Button, Card, Divider, IconButton, Stack, Typography } from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { useNotify, useTranslate } from 'react-admin';
import type { PaymentIntentTimelineEventType, PaymentOperationKind } from '@/api/models';
import { AccessDenied } from '@/components/shell/AccessDenied';
import { EmptyState } from '@/components/shell/EmptyState';
import { ErrorState } from '@/components/shell/ErrorState';
import { LoadingState } from '@/components/shell/LoadingState';
import { PageTitle } from '@/components/shell/PageTitle';
import { StatusChip } from '@/components/shell/StatusChip';
import { useCanWrite } from '@/access/useCanWrite';
import { payApiClient } from '@/api/client';
import { ApiRequestError } from '@/api/errors';
import { formatMinorUnits } from '@/utils/amount';
import { useApiResource } from '@/hooks/useApiResource';
import { capitalizeValue, gatewayCheckSituation, gatewayStatusTone, operationStatusTone, paymentStatusTone } from './domain';

type TimelineEntry =
  | { kind: 'event'; occurredAt: string; type: PaymentIntentTimelineEventType }
  | { kind: 'operation'; occurredAt: string; id: string; type: PaymentOperationKind; amount: number; status: 'pending' | 'succeeded' | 'failed'; gatewayRef?: string };

function buildTimeline(
  timeline: { type: PaymentIntentTimelineEventType; occurredAt: string }[] | undefined,
  operations: { id: string; kind: PaymentOperationKind; amount: number; status: 'pending' | 'succeeded' | 'failed'; gatewayRef?: string; occurredAt: string }[] | undefined,
): TimelineEntry[] {
  const events: TimelineEntry[] = (timeline ?? []).map((event) => ({
    kind: 'event',
    occurredAt: event.occurredAt,
    type: event.type,
  }));
  const operationEntries: TimelineEntry[] = (operations ?? []).map((operation) => ({
    kind: 'operation',
    occurredAt: operation.occurredAt,
    id: operation.id,
    type: operation.kind,
    amount: operation.amount,
    status: operation.status,
    gatewayRef: operation.gatewayRef,
  }));
  return [...events, ...operationEntries].sort((a, b) => a.occurredAt.localeCompare(b.occurredAt));
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <Stack direction="row" justifyContent="space-between" spacing={2}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 500, textAlign: 'right' }}>
        {value}
      </Typography>
    </Stack>
  );
}

export function OperationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const translate = useTranslate();
  const notify = useNotify();
  const canWrite = useCanWrite();
  const operationId = id ?? '';
  const [reconciling, setReconciling] = useState(false);
  const { data, loading, error, reload } = useApiResource(() => payApiClient.getReportOperationDetail(operationId), operationId);

  async function handleReconcile() {
    setReconciling(true);
    try {
      await payApiClient.reconcilePaymentIntent(operationId);
      notify(translate('pay.operations.detail.gatewayReconcileSuccess'), { type: 'success' });
      reload();
    } catch (caught) {
      const message = caught instanceof ApiRequestError ? caught.message : undefined;
      notify(message ?? translate('pay.errorState.body'), { type: 'error' });
    } finally {
      setReconciling(false);
    }
  }

  const backButton = (
    <IconButton onClick={() => navigate('/operations')} aria-label={translate('pay.operations.detail.back')} size="small">
      <ArrowBackOutlinedIcon fontSize="small" />
    </IconButton>
  );

  if (loading) {
    return (
      <Stack spacing={1.5}>
        <Stack direction="row" alignItems="center" spacing={1}>
          {backButton}
        </Stack>
        <Card>
          <LoadingState variant="card" />
        </Card>
      </Stack>
    );
  }

  if (error) {
    return (
      <Stack spacing={1.5}>
        <Stack direction="row" alignItems="center" spacing={1}>
          {backButton}
        </Stack>
        <Card>
          {error.errorCode === 'tenant_not_authorized' ? (
            <AccessDenied title={translate('pay.operations.detail.accessDeniedTitle')} body={error.message} />
          ) : error.errorCode === 'not_found' ? (
            <EmptyState
              title={translate('pay.operations.detail.notFoundTitle')}
              body={translate('pay.operations.detail.notFoundBody')}
            />
          ) : (
            <ErrorState body={error.message} />
          )}
        </Card>
      </Stack>
    );
  }

  if (!data) return null;

  const entries = buildTimeline(data.timeline, data.operations);
  const situation = gatewayCheckSituation(data.gatewayStatus, data.gatewayStatusCheckedAt);

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" alignItems="center" spacing={1}>
        {backButton}
        <PageTitle
          title={data.orderReference}
          subtitle={data.id}
          adornment={<StatusChip label={translate(`pay.operations.status.${data.status}`)} tone={paymentStatusTone(data.status)} />}
        />
      </Stack>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.5 }}>
        <Card sx={{ p: 2.5 }}>
          <Stack spacing={1.5}>
            <Field label={translate('pay.operations.detail.fieldTenant')} value={data.tenantCode} />
            <Field label={translate('pay.operations.detail.fieldApplication')} value={data.applicationCode} />
            <Field label={translate('pay.operations.detail.fieldType')} value={translate(`pay.operations.type.${data.type}`)} />
            <Field
              label={translate('pay.operations.detail.fieldGateway')}
              value={data.gateway ? capitalizeValue(data.gateway) : '—'}
            />
            <Divider />
            <Field label={translate('pay.operations.detail.fieldAmount')} value={formatMinorUnits(data.amount, data.currency)} />
            {data.customerRef && (
              <Field label={translate('pay.operations.detail.fieldCustomerRef')} value={data.customerRef} />
            )}
            {data.description && (
              <Field label={translate('pay.operations.detail.fieldDescription')} value={data.description} />
            )}
            <Divider />
            <Field
              label={translate('pay.operations.detail.fieldCreatedAt')}
              value={new Date(data.createdAt).toLocaleString('es-ES')}
            />
            {data.expiresAt && (
              <Field
                label={translate('pay.operations.detail.fieldExpiresAt')}
                value={new Date(data.expiresAt).toLocaleString('es-ES')}
              />
            )}
          </Stack>
        </Card>

        <Card sx={{ p: 2.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
            {translate('pay.operations.detail.timelineTitle')}
          </Typography>
          {entries.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {translate('pay.operations.detail.timelineEmpty')}
            </Typography>
          ) : (
            <Stack spacing={2}>
              {entries.map((entry, index) => (
                <Stack key={index} direction="row" spacing={1.5} alignItems="flex-start">
                  <Box sx={{ color: entry.kind === 'operation' ? 'primary.main' : 'text.secondary', pt: 0.25 }}>
                    {entry.kind === 'operation' ? (
                      <PaymentsOutlinedIcon fontSize="small" />
                    ) : (
                      <CircleOutlinedIcon sx={{ fontSize: 12 }} />
                    )}
                  </Box>
                  <Stack spacing={0.25} sx={{ flex: 1 }}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {entry.kind === 'operation'
                          ? translate(`pay.operations.operationKind.${entry.type}`)
                          : translate(`pay.operations.timelineEvent.${entry.type}`)}
                      </Typography>
                      {entry.kind === 'operation' && (
                        <StatusChip
                          label={translate(`pay.operations.operationStatus.${entry.status}`)}
                          tone={operationStatusTone(entry.status)}
                        />
                      )}
                    </Stack>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(entry.occurredAt).toLocaleString('es-ES')}
                    </Typography>
                    {entry.kind === 'operation' && (
                      <Typography variant="caption" sx={{ fontFamily: (theme) => theme.custom.fontFamilyMono }}>
                        {translate('pay.operations.detail.operationAmount')}: {formatMinorUnits(entry.amount, data.currency)}
                        {entry.gatewayRef && ` · ${translate('pay.operations.detail.operationReference')}: ${entry.gatewayRef}`}
                      </Typography>
                    )}
                  </Stack>
                </Stack>
              ))}
            </Stack>
          )}
        </Card>
      </Box>

      <Card sx={{ p: 2.5 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
          {translate('pay.operations.detail.gatewayCheckTitle')}
        </Typography>
        <Stack spacing={1.5}>
          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
            {situation === 'neverChecked' && (
              <Typography variant="body2" color="text.secondary">
                {translate('pay.operations.detail.gatewayCheckNeverChecked')}
              </Typography>
            )}
            {situation === 'unverifiable' && (
              <>
                <StatusChip label={translate('pay.operations.detail.gatewayCheckUnverifiable')} tone="warning" />
                <Typography variant="caption" color="text.secondary">
                  {translate('pay.operations.detail.gatewayCheckCheckedAt')}: {new Date(data.gatewayStatusCheckedAt as string).toLocaleString('es-ES')}
                </Typography>
              </>
            )}
            {situation === 'confirmed' && data.gatewayStatus && (
              <>
                <StatusChip
                  label={translate(`pay.operations.gatewayStatus.${data.gatewayStatus}`)}
                  tone={gatewayStatusTone(data.gatewayStatus)}
                />
                <Typography variant="caption" color="text.secondary">
                  {translate('pay.operations.detail.gatewayCheckCheckedAt')}: {new Date(data.gatewayStatusCheckedAt as string).toLocaleString('es-ES')}
                </Typography>
              </>
            )}
          </Stack>

          {canWrite &&
            (data.gatewaySupported ? (
              <Box>
                <Button size="small" variant="outlined" onClick={handleReconcile} disabled={reconciling}>
                  {translate('pay.operations.detail.gatewayReconcileAction')}
                </Button>
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {translate('pay.operations.detail.gatewayReconcileUnsupported')}
              </Typography>
            ))}
        </Stack>
      </Card>
    </Stack>
  );
}
